import React, { useState } from 'react';
import {
    addTestimonial,
    updateTestimonial,
    deleteTestimonial
} from '../../services/dataService';

const TestimonialsSection = ({ siteData, setSiteData, saving, setSaving, showSaveStatus }) => {
    const [editingTestimonials, setEditingTestimonials] = useState({});
    const [savingStates, setSavingStates] = useState({});

    const handleLocalUpdate = (testimonialId, field, value) => {
        setEditingTestimonials(prev => ({
            ...prev,
            [testimonialId]: {
                ...(prev[testimonialId] || siteData.testimonials.find(t => t.id === testimonialId)),
                [field]: value,
                hasChanges: true
            }
        }));
    };

    const handleSaveTestimonial = async (testimonialId) => {
        try {
            setSavingStates(prev => ({ ...prev, [testimonialId]: true }));
            const updatedTestimonial = editingTestimonials[testimonialId];

            await updateTestimonial(testimonialId, updatedTestimonial);

            const updatedTestimonials = siteData.testimonials.map(testimonial =>
                testimonial.id === testimonialId ? { ...updatedTestimonial, id: testimonialId } : testimonial
            );
            setSiteData(prev => ({ ...prev, testimonials: updatedTestimonials }));

            // Clear editing state
            setEditingTestimonials(prev => {
                const newState = { ...prev };
                delete newState[testimonialId];
                return newState;
            });

            showSaveStatus('Testimonial updated successfully!');
        } catch (error) {
            showSaveStatus('Failed to update testimonial', true);
        } finally {
            setSavingStates(prev => ({ ...prev, [testimonialId]: false }));
        }
    };

    const handleAddTestimonial = async () => {
        try {
            setSaving(true);
            const newTestimonial = {
                name: 'Client Name',
                text: 'Testimonial text',
                rating: 5
            };

            const testimonialId = await addTestimonial(newTestimonial);
            const testimonialWithId = { ...newTestimonial, id: testimonialId };

            setSiteData(prev => ({
                ...prev,
                testimonials: [...prev.testimonials, testimonialWithId]
            }));
            showSaveStatus('Testimonial added successfully!');
        } catch (error) {
            showSaveStatus('Failed to add testimonial', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTestimonial = async (testimonialId) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) {
            return;
        }

        try {
            setSaving(true);
            await deleteTestimonial(testimonialId);

            const updatedTestimonials = siteData.testimonials.filter(testimonial => testimonial.id !== testimonialId);
            setSiteData(prev => ({ ...prev, testimonials: updatedTestimonials }));

            // Clear editing state if exists
            setEditingTestimonials(prev => {
                const newState = { ...prev };
                delete newState[testimonialId];
                return newState;
            });

            showSaveStatus('Testimonial deleted successfully!');
        } catch (error) {
            showSaveStatus('Failed to delete testimonial', true);
        } finally {
            setSaving(false);
        }
    };

    const getTestimonialData = (testimonialId) => {
        return editingTestimonials[testimonialId] || siteData.testimonials.find(t => t.id === testimonialId);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-stone-800">Testimonials</h3>
                <button
                    onClick={handleAddTestimonial}
                    disabled={saving}
                    className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Testimonial'}
                </button>
            </div>

            <div className="space-y-4">
                {siteData.testimonials.map((testimonial) => {
                    const currentTestimonial = getTestimonialData(testimonial.id);
                    const hasChanges = editingTestimonials[testimonial.id]?.hasChanges;
                    const isSaving = savingStates[testimonial.id];

                    return (
                        <div key={testimonial.id} className="bg-stone-50 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-4">
                                <h4 className="font-medium text-stone-800">Testimonial: {currentTestimonial.name}</h4>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleSaveTestimonial(testimonial.id)}
                                        disabled={!hasChanges || isSaving}
                                        className={`px-4 py-1 rounded transition-colors text-sm ${hasChanges && !isSaving
                                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                                            : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteTestimonial(testimonial.id)}
                                        disabled={saving}
                                        className="text-red-500 hover:text-red-700 disabled:opacity-50 px-2"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={currentTestimonial.name}
                                        onChange={(e) => handleLocalUpdate(testimonial.id, 'name', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Rating</label>
                                    <select
                                        value={currentTestimonial.rating}
                                        onChange={(e) => handleLocalUpdate(testimonial.id, 'rating', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    >
                                        <option value={1}>1 Star</option>
                                        <option value={2}>2 Stars</option>
                                        <option value={3}>3 Stars</option>
                                        <option value={4}>4 Stars</option>
                                        <option value={5}>5 Stars</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-stone-700 mb-1">Testimonial Text</label>
                                <textarea
                                    value={currentTestimonial.text}
                                    onChange={(e) => handleLocalUpdate(testimonial.id, 'text', e.target.value)}
                                    className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    rows="3"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TestimonialsSection;